import { z } from 'zod';
import { Currency } from '../types/Currency.types';
import { PaginationSchema } from '../types/Pagination.types';
import { UserDtoSchema } from '../user/user.schema';

// Wallet DTO Schema
export const WalletDtoSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  bonusSweepCoin: z.number(),
  ftdAmount: z.number(),
  sweepCoin: z.number(), // paid coin balance
  goldCoin: z.number(), // bonus coin balance
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const WalletDetailsDtoSchema = z.object({
  id: z.string(),
  userId: z.string(),
  sweepCoin: z.number(),
  goldCoin: z.number(),
  bonusSweepCoin: z.number(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  totalRedeemable: z.number(),
  redeemableToday: z.number(),
  unplayedSweepCoin: z.number(),
  unplayedBonusSweepCoin: z.number(),
  requiredSweepCoinWager: z.number(),
  requiredBonusSweepCoinWager: z.number(),
});

export const WalletTransactionDtoSchema = z.object({
  id: z.string().uuid(),
  walletId: z.string().uuid(),
  userId: z.string().uuid(),
  type: z.enum(['deposit', 'withdraw']),
  amount: z.number(), // Amount of coin in cents
  currency: z.nativeEnum(Currency),
  balanceAfter: z.number(),
  externalId: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const WalletTransactionPaginationSchema = PaginationSchema(WalletTransactionDtoSchema);

// Full transaction payload with expanded relations.
export const WalletTransactionFullDtoSchema = WalletTransactionDtoSchema.extend({
  wallet: WalletDtoSchema,
  user: UserDtoSchema,
});

export const WalletTransactionFullPaginationSchema = PaginationSchema(
  WalletTransactionFullDtoSchema,
);

// Used for socket events
export const WalletBalanceMessageSchema = z.object({
  userId: z.string().uuid(),
  walletId: z.string().uuid(),
  bonusSweepCoin: z.string(), // bonus coin balance
  ftdAmount: z.string(),
  sweepCoin: z.string(), // paid coin balance
  goldCoin: z.string(), // bonus coin balance
});

export const WalletInsufficientBalanceMessageSchema = z.object({
  userId: z.string().uuid(),
  walletId: z.string().uuid(),
  currency: z.nativeEnum(Currency),
  requestedAmount: z.coerce.number(),
  availableBalance: z.coerce.number(),
  goldCoinBalance: z.coerce.number(),
  sweepCoinBalance: z.coerce.number(),
  bonusSweepCoinBalance: z.coerce.number(),
});

export const WalletBalanceAutoTopupMessageSchema = z.object({
  userId: z.string().uuid(),
  walletId: z.string().uuid(),
  currency: z.nativeEnum(Currency),
  topupAmount: z.coerce.number(),
  balanceBefore: z.coerce.number(),
  balanceAfter: z.coerce.number(),
  sweepCoin: z.coerce.number(),
  goldCoin: z.coerce.number(),
  bonusSweepCoin: z.coerce.number(),
});

export type WalletDto = z.infer<typeof WalletDtoSchema>;
export type WalletTransactionDto = z.infer<typeof WalletTransactionDtoSchema>;
export type WalletTransactionPaginationDto = z.infer<typeof WalletTransactionPaginationSchema>;
export type WalletTransactionFullDto = z.infer<typeof WalletTransactionFullDtoSchema>;
export type WalletTransactionFullPaginationDto = z.infer<typeof WalletTransactionFullPaginationSchema>;
export type WalletBalanceMessageDto = z.infer<typeof WalletBalanceMessageSchema>;
export type WalletInsufficientBalanceMessageDto = z.infer<typeof WalletInsufficientBalanceMessageSchema>;
export type WalletBalanceAutoTopupMessageDto = z.infer<typeof WalletBalanceAutoTopupMessageSchema>;
